import type { RecStatusResponse, RpmPercentResponse } from '@on3/api';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import { TextLink } from '@public/components/UI/Link/TextLink';
import clsx from 'clsx';
import { format } from 'date-fns';

import styles from './HighSchoolItemStatus.module.scss';

interface IPlayerRankingItemStatusProps {
  status?: RecStatusResponse;
  slug?: string | null;
  sport?: string | null;
  predictions?: RpmPercentResponse[] | null;
  recruitmentKey?: number | null;
  year?: string | null;
  className?: string;
}

export const HighSchoolItemStatus = ({
  status,
  slug,
  predictions,
  recruitmentKey,
  sport,
  className,
  year,
}: IPlayerRankingItemStatusProps) => {
  const isCommitted = status?.type === 'Committed';
  const isEnrolled = status?.type === 'Enrolled';
  const isSigned = status?.type === 'Signed';
  const hasStatus = isCommitted || isEnrolled || isSigned;
  const committedOrganizationAssetUrl = status?.committedAsset?.assetUrl;
  const committedOrganizationSlug = status?.committedAsset?.slug;
  const hasPredictions = !!predictions?.length;
  const committedColor = status?.committedAsset?.primaryColor || '#e4e8f2';
  const statusDate = status?.date && new Date(status.date);
  const formattedDate = statusDate && format(statusDate, 'M/d/yyy');

  return (
    <div className={clsx(styles.statusWrapper, className)}>
      {hasStatus && (
        <div className={styles.statusContainer}>
          <a
            className={styles.logoContainer}
            href={`/college/${committedOrganizationSlug}/${sport}/${year}/industry-comparison-commits/`}
            style={{ borderColor: committedColor }}
          >
            <TeamAvatar
              className={styles.committedLogo}
              height={20}
              slug={committedOrganizationSlug}
              src={committedOrganizationAssetUrl}
              width={20}
            />
          </a>
          {hasStatus && (
            <div className={styles.currentStatusContainer}>
              <span className={styles.currentStatus}>
                {isEnrolled && 'Enrolled'}
                {isCommitted && 'Hard Commit'}
                {isSigned && 'Signed'}
              </span>
              {!!formattedDate && (
                <span className={styles.statusDate}>{formattedDate}</span>
              )}
            </div>
          )}
        </div>
      )}
      {!hasStatus && hasPredictions && (
        <TextLink
          className={styles.predictionsContainer}
          href={`/rivals/${slug}/recruiting/${recruitmentKey}/rpm/`}
          underline="none"
        >
          {predictions?.slice(0, 2).map((org) => {
            return (
              <div className={styles.prediction} key={org.organization?.key}>
                <TeamAvatar
                  className={styles.predictionLogo}
                  height="20"
                  slug={org?.organization?.slug}
                  src={org?.organization?.assetUrl}
                  width="20"
                />
                {org.percent && (
                  <span className={styles.rpmPercent}>
                    {Math.round(org.percent)}%
                  </span>
                )}
              </div>
            );
          })}
        </TextLink>
      )}
    </div>
  );
};
